import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import axios from "axios";
import type { ApiResponse } from "../types";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
    ? connection_development
    : process.env.REACT_APP_NODE_ENV === "production"
    ? connection_production
    : connection_local;

/**
 * Learner bootstrap — single call that returns personalStatus, earnedAchievementDTOList,
 * thisWeekRankingDTOList, allTimeRankingDTOList, taskItemProgressDTOList.
 * Replaces the five separate fetches LearnerApp used to fire on dashboard mount.
 */
export const retrieveLearnerGamificationBootstrap = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification.bootstrap.context}${endpoints.gamification.bootstrap.learner.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Points-status mini-aggregate — personalStatus + both season rankings in one call. Fired after
 * every "update-points" websocket event (previously three separate requests per answered activity).
 */
export const retrieveLearnerPointsStatus = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification.bootstrap.context}${endpoints.gamification.bootstrap.learner["points-status"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Achievement catalog — every achievement family with its tier rules (level + threshold). Static/global
 * reference data; the achievement-collection UI uses it to draw progress against the REAL award thresholds
 * instead of a hardcoded copy. See GamificationUtilization.setAchievementRuleCatalog /
 * retrieveAchievementRuleListByParams.
 */
export const retrieveAchievementCatalog = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.gamification.url}${endpoints.gamification.achievements.context}${endpoints.gamification.achievements.query.dto.all.context}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Earned Points
 */
export const queryEarnedPointDTOListByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["earned-points"].context}${endpoints.gamification["earned-points"].query.dto["by-params"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Earned Achievements
 */
export const queryEarnedAchievementDTOListByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["earned-achievements"].context}${endpoints.gamification["earned-achievements"].query.dto["by-params"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Sets the learner's profile showcase (VİTRİN) — the ordered achievement ids to feature, plus the chosen
 * tier per achievement (showcaseLevelList, parallel to achievementIdList; lets a learner showcase a lower tier).
 * Returns the learner's full, refreshed earned-achievement list (with showcaseOrder + showcaseLevel set).
 * setParams: { userId, userProfileId, achievementIdList, showcaseLevelList }
 */
export const setEarnedAchievementShowcase = async (setParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["earned-achievements"].context}${endpoints.gamification["earned-achievements"].showcase.context}`,
      setParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Replace-sets the learner's personalized weekly goals and returns the refreshed progress list
 * (LearnerGoalProgressDTO[]: metric, targetValue, currentValue, completed) for the current week.
 * setParams: { userId, userProfileId, seasonId, weekBeginAt, weekEndAt, goalList: [{ metric, targetValue }] }
 */
export const setLearnerGoals = async (setParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["learner-goals"].context}${endpoints.gamification["learner-goals"].set.context}`,
      setParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Statuses
 */
export const queryPersonalStatusByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification.statuses.context}${endpoints.gamification.statuses.personal.context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryPersonalStatusByParamsWithToken = async (
  queryParams,
  token
): Promise<ApiResponse> => {
  try {
    // A LOCAL instance, like solveActivityWithAIOnlyImageWithToken: on a phone browser that is itself
    // signed in, the global interceptor would overwrite this explicit token with that browser's own
    // session — reading somebody else's points.
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.gamification.url}${endpoints.gamification.statuses.context}${endpoints.gamification.statuses.personal.context}`,
      queryParams,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Market
 */
export const queryMarketDTOByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification.markets.context}${endpoints.gamification.markets.query.dto["by-params"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Market Item Purchases
 */
export const createMarketItemPurchaseByParams = async (createParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["market-item-purchases"].context}${endpoints.gamification["market-item-purchases"]["by-params"].context}`,
      createParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryMarketItemPurchaseDTOListByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["market-item-purchases"].context}${endpoints.gamification["market-item-purchases"].query.dto["by-params"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Mascot Inventory Item
 */
export const createMascotInventoryItem = async (createParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["mascot-inventory-items"].context}/`,
      createParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const patchMascotInventoryItem = async (patchParams): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.gamification.url}${endpoints.gamification["mascot-inventory-items"].context}/`,
      patchParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const deleteMascotInventoryItem = async (mascotInventoryItemId): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(
      `${connection.base.gamification.url}${endpoints.gamification["mascot-inventory-items"].context}${endpoints.gamification["mascot-inventory-items"].id.context}`.replace(
        "{id}",
        mascotInventoryItemId
      )
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryMascotInventoryItemDTOListByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["mascot-inventory-items"].context}${endpoints.gamification["mascot-inventory-items"].query.dto["by-params"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Tasks
 */
export const queryTaskItemProgressDTOListByParams = async (
  queryParams
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["tasks"].context}${endpoints.gamification["tasks"].query.dto["by-params"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const initializeTaskItemProgressDTO = async (
  initializeParams
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["task-item-progresses"].context}${endpoints.gamification["task-item-progresses"].initialize.context}`,
      initializeParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Leagues
 */
export const queryLeagueItemProgressDTOListInSameLeague = async (
  queryParams
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.gamification.url}${endpoints.gamification["league-item-progresses"].context}${endpoints.gamification["league-item-progresses"].query.dto["in-same-league"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};
